import { cn } from './utils';

export const TABLE_ROLES = [
  'identity',
  'text',
  'numeric',
  'currency',
  'percentage',
  'date',
  'status',
  'selection',
  'action',
] as const;

export type TableRole = (typeof TABLE_ROLES)[number];
export type TableAlign = 'left' | 'center' | 'right';

export interface TableRoleMeta {
  role?: TableRole | undefined;
  align?: TableAlign | undefined;
  numeric?: boolean | undefined;
}

const RIGHT_ALIGNED_ROLES = new Set<TableRole>(['numeric', 'currency', 'percentage', 'date']);

const CENTER_ALIGNED_ROLES = new Set<TableRole>(['status', 'selection', 'action']);

export function resolveTableAlign(meta: TableRoleMeta | undefined): TableAlign {
  if (meta?.align) return meta.align;
  if (meta?.role && RIGHT_ALIGNED_ROLES.has(meta.role)) return 'right';
  if (meta?.role && CENTER_ALIGNED_ROLES.has(meta.role)) return 'center';
  if (meta?.numeric) return 'right';
  return 'left';
}

export function isTableDataRole(meta: TableRoleMeta | undefined): boolean {
  return Boolean(meta?.numeric) || Boolean(meta?.role && RIGHT_ALIGNED_ROLES.has(meta.role));
}

export function getTableAlignClass(align: TableAlign): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

export function getTableContentClass(
  align: TableAlign,
  dataRole = false,
  clip = true
): string {
  return cn(
    'crm-table-content flex min-w-0 w-full max-w-full items-center gap-1',
    clip ? 'overflow-hidden' : 'overflow-visible',
    align === 'right' && 'justify-end text-right',
    align === 'center' && 'justify-center text-center',
    align === 'left' && 'justify-start text-left',
    dataRole && 'font-mono tabular-nums'
  );
}

export interface ColumnRailSize {
  size: number;
  minSize: number;
}

export function getDefaultColumnRail(role: TableRole | undefined): ColumnRailSize | null {
  switch (role) {
    case 'selection':
      return { size: 44, minSize: 44 };
    case 'action':
      return { size: 168, minSize: 140 };
    case 'status':
      return { size: 144, minSize: 120 };
    case 'date':
      return { size: 144, minSize: 128 };
    case 'numeric':
    case 'currency':
    case 'percentage':
      return { size: 176, minSize: 148 };
    case 'text':
      return { size: 176, minSize: 140 };
    case 'identity':
      return null;
    default:
      return { size: 160, minSize: 128 };
  }
}

export function applyColumnRails<
  T extends { size?: number; minSize?: number; meta?: TableRoleMeta },
>(columns: T[]): T[] {
  let leftoverAssigned = false;
  return columns.map((column) => {
    if (column.size !== undefined || column.minSize !== undefined) {
      if (column.meta?.role === 'identity') leftoverAssigned = true;
      return column;
    }
    if (column.meta?.role === 'identity' && !leftoverAssigned) {
      leftoverAssigned = true;
      return { ...column, minSize: 220 };
    }
    const rail = getDefaultColumnRail(column.meta?.role) ?? getDefaultColumnRail('text');
    if (!rail) return column;
    return { ...column, size: rail.size, minSize: rail.minSize };
  });
}

export function getTableRailClass(role: TableRole | undefined): string | undefined {
  if (role === 'selection') return 'crm-data-table__selection';
  if (role === 'action') return 'crm-data-table__action';
  return undefined;
}
